import { useInfiniteQuery, useQueryClient } from "react-query";

import { minutesToMilliseconds } from "date-fns";

import { API_ROUTES } from "config/routes";

import { mapURLSearchParams } from "utils/query";

import { Client } from "services/store/Store.interface";

import fetcher from "lib/fetchJson";

interface UseMarketplaceListProps {
  limit?: number;
  additionalFilters?: Record<string, string>;
}

const useMarketplaceList = (props: UseMarketplaceListProps) => {
  const queryClient = useQueryClient();
  const { limit = 6, additionalFilters } = props || {};

  const {
    fetchNextPage,
    hasNextPage,
    data,
    isLoading,
    isFetching,
    isStale,
    isRefetching,
  } = useInfiniteQuery(
    ["marketplaces", { limit, ...additionalFilters }],
    async ({ pageParam = 1 }) => {
      const params = mapURLSearchParams({
        limit,
        page: pageParam,
        ...(additionalFilters || {}),
        "orderBy[publishDate]": "desc",
      });

      const response = await fetcher<{
        data: Client[];
        page: number;
        limit: number;
        totalPages: number;
        total: number;
      }>(`${API_ROUTES.MARKETPLACE_LIST}?${params}`);

      return {
        items: response.data,
        totalPages: response.totalPages,
        nextPage: (pageParam < response.totalPages
          ? pageParam + 1
          : undefined) as number,
      };
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextPage,
      staleTime: minutesToMilliseconds(15),
    },
  );

  if (isRefetching && isStale) {
    queryClient.resetQueries({ queryKey: "marketplaces" });
  }

  const items = data?.pages.reduce<Client[]>((acc, page) => {
    return [...acc, ...page.items];
  }, []);

  return {
    items: items || [],
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetching,
  };
};

export default useMarketplaceList;
